import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于虚空韩漫网 - 免费韩漫数字图书馆',
    description: '虚空韩漫网集免费韩漫、漫画大全、在线漫画和好看的韩国漫画于一体，致力于打造最全面的韩漫数字图书馆。',
    url: 'http://txtjly.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '虚空韩漫网',
      url: 'http://txtjly.com/',
      description: '虚空韩漫网集免费韩漫、漫画大全、在线漫画和好看的韩国漫画于一体，致力于打造最全面的韩漫数字图书馆。',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '碧波路888号',
        addressLocality: '上海市',
        addressRegion: '浦东新区',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '021-5888-6666',
        contactType: 'customer service',
      },
      sameAs: ['http://txtjly.com/'],
    },
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = (formData.get('website_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setTimeout(() => {
      setFormStatus('success');
      e.currentTarget.reset();
    }, 500);
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 虚空韩漫网 | 免费韩漫数字图书馆"
        description="虚空韩漫网集免费韩漫、漫画大全、在线漫画和好看的韩国漫画于一体。了解我们的品牌故事、用户口碑和联系方式，致力于打造最全面的韩漫数字图书馆。"
        keywords="虚空韩漫网,关于我们,韩漫网站,免费韩漫,漫画图书馆"
        schema={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]} />
        </div>

        {/* Brand Story */}
        <section className="py-16 md:py-24 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 border border-foreground-300 text-foreground-600 text-xs font-medium rounded-full whitespace-nowrap">
              品牌故事
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mt-6 leading-tight">
              打造最全面的
              <br />
              韩漫数字图书馆
            </h1>
            <div className="mt-8 max-w-2xl mx-auto text-left space-y-5">
              <p className="text-foreground-600 leading-relaxed">
                虚空韩漫网诞生于2024年，由一群热爱韩国漫画的年轻人共同创立。我们深知韩漫爱好者在寻找高质量中文资源时面临的困难 —— 翻译不完整、更新不及时、阅读体验差。因此，我们决定打造一个真正属于华语韩漫爱好者的数字家园。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                经过两年多的发展，虚空韩漫网已经从一个小众漫画分享站成长为拥有数十万日活用户的综合性韩漫平台。我们收录了涵盖<strong>恋爱、奇幻、动作、悬疑、搞笑、剧情</strong>等全品类韩国漫画，每日更新热门连载，为用户提供高清流畅的在线阅读体验。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                我们的使命很简单：让每一位韩漫爱好者都能在这里找到自己喜欢的作品，享受阅读的乐趣。无论你是资深韩漫迷还是刚刚入坑的新人，虚空韩漫网都会是你最好的伙伴。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { number: '500+', label: '收录作品', icon: 'ri-book-2-line' },
                { number: '50万+', label: '日活用户', icon: 'ri-user-heart-line' },
                { number: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
                { number: '24h', label: '更新保障', icon: 'ri-time-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {stat.number}
                  </p>
                  <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-24 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-3/5">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-foreground-900"></div>
                  <span className="text-sm font-medium text-foreground-600">用户口碑</span>
                </div>

                {testimonials.map((t, index) => (
                  <div key={t.id} className={`mb-8 ${index > 0 ? 'pt-6 border-t border-background-200' : ''}`}>
                    <p className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 leading-relaxed">
                      <span className="text-foreground-300 text-3xl">"</span>
                      {t.content}
                      <span className="text-foreground-300 text-3xl">"</span>
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:w-2/5">
                <div className="sticky top-24">
                  <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Artistic%20Korean%20manhwa%20style%20illustration%20of%20a%20cozy%20library%20reading%20scene%2C%20warm%20ambient%20lighting%2C%20bookshelves%20with%20colorful%20books%2C%20peaceful%20atmosphere%2C%20Korean%20digital%20art%20style%2C%20soft%20cream%20and%20warm%20brown%20color%20palette%2C%20no%20characters%2C%20clean%20composition&width=600&height=800&seq=about-team-art&orientation=portrait"
                      alt="虚空韩漫网 品牌故事"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              联系与合作
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              如果您有任何问题、合作意向或建议，欢迎通过以下方式联系我们
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-3 px-6 py-4 bg-background-50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div className="text-left">
                  <p className="text-xs text-foreground-400">邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">hello@txtjly.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-6 py-4 bg-background-50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div className="text-left">
                  <p className="text-xs text-foreground-400">电话</p>
                  <p className="text-sm font-medium text-foreground-800">021-5888-6666</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <form
              onSubmit={handleSubmit}
              className="mt-10 max-w-xl mx-auto bg-background-50 rounded-2xl p-6 md:p-8 text-left"
             
            >
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                给我们留言
              </h3>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-background-100 rounded-lg text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-background-100 rounded-lg text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-background-100 rounded-lg text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
              </div>

              <button
                type="submit"
                className="mt-6 w-full py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-green-600 text-center">
                  留言已发送成功！我们会尽快回复您。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-red-500 text-center">
                  {formError || '发送失败，请稍后再试。'}
                </p>
              )}
            </form>
          </div>
        </section>

        {/* FAQ for About Page */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {[
                { q: '虚空韩漫网的内容来源是什么？', a: '我们的漫画内容均来自正版授权渠道和公开的韩漫资源，经过团队精心筛选和整理后呈现给读者。' },
                { q: '如何与虚空韩漫网合作？', a: '如果您是漫画作者、翻译组或版权方，欢迎通过上方联系方式与我们洽谈合作，共同为韩漫爱好者提供更优质的内容。' },
                { q: '虚空韩漫网有移动APP吗？', a: '我们正在积极开发移动APP，届时将为用户提供更便捷的移动阅读体验。目前您可以通过手机浏览器访问我们的网站，同样支持完美的移动端阅读。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-900 font-medium text-sm list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}